import {type ReactNode, useId} from "react";
import {useMessages} from "i18n";
import {PeelConfetti} from "./PeelTag";
import {TicketShadow, Ticket, TicketClaim, TicketEyebrow, TicketHeading, TicketMain, TicketMeta, TicketPerforation, TicketReopen, TicketStub, TicketStubLabel, TicketTerms, TicketValue} from "./PeelTicket.styles";

/** The dialog omits the already-torn stub; the success ticket keeps its claim control. */
export function PeelTicket({title, value, terms, metadata, stubLabel, compactValue = false, claimed, disabled = false, onClaim, stub = true, eyebrow, claimLabel, claimAria}: {
	title: string;
	value: string;
	/** The line under the value, saying what the value is: "First month" beside a first-month amount. */
	terms: string;
	metadata: ReactNode;
	stubLabel: string;
	compactValue?: boolean;
	claimed: boolean;
	disabled?: boolean;
	onClaim?: () => void;
	stub?: boolean;
	/** Overrides the generic "Special coupon" print — the promo dialog names its platform here. */
	eyebrow?: string;
	/**
	 * Overrides the stub's generic "Claim" print and its accessible name: a first-month offer printed on
	 * this ticket keeps its own action, "Get the offer" or "Keep my plan" (ui#2949).
	 */
	claimLabel?: string;
	claimAria?: string;
}) {
	const {offerLadder} = useMessages();
	const headingId = useId();
	const labels = <><TicketClaim>{claimLabel ?? offerLadder.gift.claim}</TicketClaim><TicketStubLabel>{stubLabel}</TicketStubLabel></>;
	return <TicketShadow><Ticket role="group" aria-labelledby={headingId} data-testid="peel-ticket" data-torn={claimed} $stub={stub}>
		<TicketMain>
			<TicketEyebrow>{eyebrow ?? offerLadder.gift.eyebrow}</TicketEyebrow>
			<TicketHeading id={headingId} aria-label={title}>
				<TicketValue $compact={compactValue}>{value}</TicketValue>
				<TicketTerms>{terms}</TicketTerms>
			</TicketHeading>
			<TicketMeta>{metadata}</TicketMeta>
			{claimed && !disabled && onClaim !== undefined && <TicketReopen type="button" aria-label={offerLadder.gift.reopenAria} data-ticket-reopen="" onClick={onClaim}/>}
		</TicketMain>
		<TicketPerforation aria-hidden="true" data-ticket-perforation/>
		{stub && <>
			{onClaim === undefined
				? <TicketStub as="div" $torn={claimed}>{labels}</TicketStub>
				: <TicketStub as="button" type="button" aria-label={claimAria ?? claimLabel ?? offerLadder.gift.claimAria} disabled={disabled}
					$torn={claimed} inert={claimed} aria-hidden={claimed} onClick={onClaim}>{labels}</TicketStub>}
		</>}
		{onClaim !== undefined && <PeelConfetti active={claimed}/>}
	</Ticket></TicketShadow>;
}
